import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCompanies,
  createCompany,
  fetchCompany,
  updateCompany,
  fetchCompanyDashboard,
  fetchCompanyInvoices,
  createCompanyInvoice,
  updateCompanyInvoice,
  type CompaniesQuery,
  type CreateCompanyPayload,
  type UpdateCompanyPayload,
  type CompanyInvoicesQuery,
  type CreateInvoicePayload,
  type UpdateInvoicePayload,
} from '@/services/api/companies.api';

// ─── Companies ───────────────────────────────────────────────────────────────

export function useCompanies(query?: CompaniesQuery) {
  return useQuery({
    queryKey: ['companies', query],
    queryFn: () => fetchCompanies(query),
    staleTime: 30_000,
  });
}

export function useCompany(id: string | null) {
  return useQuery({
    queryKey: ['companies', id],
    queryFn: () => fetchCompany(id as string),
    staleTime: 30_000,
    enabled: id !== null,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCompanyPayload) => createCompany(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCompanyPayload }) =>
      updateCompany(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}

export function useCompanyDashboard(id: string | null) {
  return useQuery({
    queryKey: ['companies', id, 'dashboard'],
    queryFn: () => fetchCompanyDashboard(id as string),
    staleTime: 30_000,
    enabled: id !== null,
  });
}

// ─── Invoices ────────────────────────────────────────────────────────────────

export function useCompanyInvoices(companyId: string | null, query?: CompanyInvoicesQuery) {
  return useQuery({
    queryKey: ['companies', companyId, 'invoices', query],
    queryFn: () => fetchCompanyInvoices(companyId as string, query),
    staleTime: 30_000,
    enabled: companyId !== null,
  });
}

export function useCreateCompanyInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, payload }: { companyId: string; payload: CreateInvoicePayload }) =>
      createCompanyInvoice(companyId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}

export function useUpdateCompanyInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      invoiceId,
      payload,
    }: {
      companyId: string;
      invoiceId: string;
      payload: UpdateInvoicePayload;
    }) => updateCompanyInvoice(companyId, invoiceId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}
